import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, RotateCcw, Star, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";
import { StatusChip } from "./ResidentHelpdesk";

const TYPE_LABELS = {
  COMPLAINT: "Complaint", SERVICE_REQUEST: "Service request", MAINTENANCE_REQUEST: "Maintenance",
  COMMON_AREA_ISSUE: "Common area issue", SECURITY_ISSUE: "Security", OTHER: "Other",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const dlUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/resident/helpdesk/attachments/${id}/download`;
const isImage = (ct) => (ct || "").startsWith("image/");

function Field({ label, value, testId }) {
  return <div><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><p className="mt-0.5 break-words text-sm font-medium" data-testid={testId}>{value || "—"}</p></div>;
}

export default function ResidentTicketDetail() {
  const { ticketId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replyFile, setReplyFile] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [showReopen, setShowReopen] = useState(false);
  const [rating, setRating] = useState(0);
  const [fbComment, setFbComment] = useState("");

  const load = useCallback(() => {
    api.get(`/resident/helpdesk/tickets/${ticketId}`).then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This request is unavailable."));
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Write a reply first");
    setSending(true);
    try {
      await api.post(`/resident/helpdesk/tickets/${ticketId}/comments`, { text: reply.trim() });
      if (replyFile) {
        const fd = new FormData(); fd.append("file", replyFile);
        await api.post(`/resident/helpdesk/tickets/${ticketId}/attachments`, fd).catch(() => toast.error("Reply saved but attachment failed"));
      }
      setReply(""); setReplyFile(null); toast.success("Reply added"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add reply"); }
    finally { setSending(false); }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) return toast.error("A reopen reason is required");
    try {
      await api.post(`/resident/helpdesk/tickets/${ticketId}/reopen`, { reason: reopenReason.trim() });
      setReopenReason(""); setShowReopen(false); toast.success("Request reopened"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reopen"); }
  };

  const submitFeedback = async () => {
    if (!(rating >= 1 && rating <= 5)) return toast.error("Choose a rating");
    try {
      await api.post(`/resident/helpdesk/tickets/${ticketId}/feedback`, { rating, comment: fbComment.trim() });
      toast.success("Thanks for your feedback"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit feedback"); }
  };

  if (error) return <div><Link className="inline-flex items-center gap-1 text-sm text-primary" to="/resident/helpdesk"><ArrowLeft className="h-4 w-4" /> Back</Link><p className="mt-4 border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600" data-testid="resident-ticket-detail-error">{error}</p></div>;
  if (!data) return <Loading />;

  const { ticket, comments, attachments } = data;
  const canReply = !["CLOSED", "CANCELLED"].includes(ticket.status);
  const canReopen = ["RESOLVED", "CLOSED"].includes(ticket.status);
  const canFeedback = ticket.status === "CLOSED" && !ticket.feedback;

  return (
    <div data-testid="resident-ticket-detail">
      <Link className="inline-flex items-center gap-1 text-sm text-primary" data-testid="resident-ticket-back" to="/resident/helpdesk"><ArrowLeft className="h-4 w-4" /> My requests</Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid="resident-ticket-number">{ticket.ticket_number}</p>
          <h1 className="mt-1 break-words font-display text-3xl font-bold" data-testid="resident-ticket-subject">{ticket.subject}</h1>
        </div>
        <StatusChip status={ticket.status} />
      </div>

      {ticket.status === "WAITING_FOR_RESIDENT" && <p className="mt-3 rounded-md bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700" data-testid="resident-ticket-action-required">Action required — the team is waiting for your response.</p>}

      <section className="mt-5 grid gap-4 border border-slate-200 bg-white p-5 sm:grid-cols-3">
        <Field label="Type" testId="resident-ticket-type" value={TYPE_LABELS[ticket.ticket_type] || ticket.ticket_type} />
        <Field label="Category" testId="resident-ticket-category" value={ticket.category_name} />
        <Field label="Priority" testId="resident-ticket-priority" value={ticket.priority} />
        <Field label="Location" testId="resident-ticket-location" value={ticket.location_type === "UNIT" ? (ticket.unit_number || "Unit") : `Common area${ticket.location ? ` · ${ticket.location}` : ""}`} />
        <Field label="Raised on" testId="resident-ticket-created" value={fmt(ticket.created_at)} />
        <Field label="Last update" testId="resident-ticket-updated" value={fmt(ticket.updated_at || ticket.created_at)} />
      </section>

      {ticket.description && <section className="mt-4 border border-slate-200 bg-white p-5"><p className="text-xs uppercase tracking-wide text-slate-400">Description</p><p className="mt-1 whitespace-pre-wrap break-words text-sm" data-testid="resident-ticket-description">{ticket.description}</p></section>}

      {(ticket.resolution_summary || ticket.resolved_at) && (
        <section className="mt-4 border border-emerald-200 bg-emerald-50 p-5" data-testid="resident-ticket-resolution">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Resolution</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-emerald-900">{ticket.resolution_summary || "This request was marked resolved."}</p>
          {ticket.resolved_at && <p className="mt-1 text-xs text-emerald-700">Resolved on {fmt(ticket.resolved_at)}</p>}
        </section>
      )}

      <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-attachments">
        <p className="text-xs uppercase tracking-wide text-slate-400">Attachments</p>
        {attachments.length ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {attachments.map((a) => (
              <div className="rounded-md border border-slate-200 p-3" data-testid={`resident-attachment-${a.attachment_id}`} key={a.attachment_id}>
                {isImage(a.content_type) && <img alt={a.filename} className="mb-2 max-h-40 w-full rounded object-cover" src={dlUrl(a.attachment_id)} />}
                <a className="inline-flex items-center gap-1 text-sm text-primary" data-testid={`resident-attachment-download-${a.attachment_id}`} href={dlUrl(a.attachment_id)} rel="noreferrer" target="_blank"><Download className="h-4 w-4" /> {a.filename}</a>
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-sm text-slate-500">No attachments.</p>}
      </section>

      <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-timeline">
        <p className="text-xs uppercase tracking-wide text-slate-400">Activity</p>
        <ol className="mt-3 space-y-3">
          {(ticket.timeline || []).map((ev) => (
            <li className="flex gap-3 text-sm" key={ev.event_id}>
              <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
              <div><p className="font-medium">{ev.kind.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}{ev.detail ? ` — ${ev.detail}` : ""}</p><p className="text-xs text-slate-500">{fmt(ev.at)}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-comments">
        <p className="text-xs uppercase tracking-wide text-slate-400">Conversation</p>
        <div className="mt-3 space-y-3">
          {comments.length ? comments.map((c) => (
            <div className="rounded-md bg-slate-50 p-3 text-sm" data-testid={`resident-comment-${c.comment_id}`} key={c.comment_id}>
              <p className="whitespace-pre-wrap break-words">{c.text}</p>
              <p className="mt-1 text-xs text-slate-500">{c.created_by_name || "Staff"} · {fmt(c.created_at)}</p>
            </div>
          )) : <p className="text-sm text-slate-500">No replies yet.</p>}
        </div>
        {canReply && (
          <div className="mt-4 space-y-2">
            <Textarea data-testid="resident-reply-text" onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" value={reply} />
            <label className="flex items-center gap-2 text-sm"><Paperclip className="h-4 w-4 text-slate-500" /><input accept="image/jpeg,image/png,image/webp,application/pdf" data-testid="resident-reply-attachment" onChange={(e) => setReplyFile(e.target.files?.[0] || null)} type="file" /></label>
            <Button data-testid="resident-reply-submit" disabled={sending} onClick={sendReply}><Send className="h-4 w-4" /> {sending ? "Sending…" : "Send reply"}</Button>
          </div>
        )}
      </section>

      {canReopen && (
        <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-reopen">
          {!showReopen ? (
            <Button data-testid="resident-reopen-toggle" onClick={() => setShowReopen(true)} variant="outline"><RotateCcw className="h-4 w-4" /> Reopen request</Button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium">Why are you reopening this request?</p>
              <Textarea data-testid="resident-reopen-reason" onChange={(e) => setReopenReason(e.target.value)} placeholder="Reason for reopening" value={reopenReason} />
              <Button data-testid="resident-reopen-submit" onClick={reopen}><RotateCcw className="h-4 w-4" /> Confirm reopen</Button>
            </div>
          )}
        </section>
      )}

      {ticket.feedback && (
        <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-feedback-shown">
          <p className="text-xs uppercase tracking-wide text-slate-400">Your feedback</p>
          <div className="mt-1 flex items-center gap-1">{[1, 2, 3, 4, 5].map((n) => <Star className={`h-4 w-4 ${n <= ticket.feedback.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} key={n} />)}</div>
          {ticket.feedback.comment && <p className="mt-2 text-sm">{ticket.feedback.comment}</p>}
        </section>
      )}

      {canFeedback && (
        <section className="mt-4 border border-slate-200 bg-white p-5" data-testid="resident-ticket-feedback">
          <p className="text-sm font-medium">Rate how this request was handled</p>
          <div className="mt-2 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button data-testid={`resident-feedback-star-${n}`} key={n} onClick={() => setRating(n)} type="button"><Star className={`h-6 w-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>
            ))}
          </div>
          <Textarea className="mt-3" data-testid="resident-feedback-comment" onChange={(e) => setFbComment(e.target.value)} placeholder="Optional comment" value={fbComment} />
          <Button className="mt-3" data-testid="resident-feedback-submit" onClick={submitFeedback}>Submit feedback</Button>
        </section>
      )}
    </div>
  );
}
